import axiosInstance from "./baseUrl";
import {
  CouponListResponse,
  ListCouponsQuery,
  UsedCouponListResponse,
} from "../redux/features/types/couponListTypes";

// Get all coupons
export const getAllCouponsSummary = async () => {
  const response = await axiosInstance.get("/v1/coupon/coupon_summary");
  return response.data;
};

// Get all coupons
export const getAllCoupons = async (
  params: ListCouponsQuery
): Promise<CouponListResponse> => {
  const response = await axiosInstance.get("/v1/coupon/list_coupons", {
    params,
  });
  return response.data;
};

// Get used coupons. Filter set mirrors GET /v1/coupon/list_coupons exactly.
export const getUsedCoupons = async (
  params: ListCouponsQuery
): Promise<UsedCouponListResponse> => {
  const response = await axiosInstance.get("/v1/coupon/used_coupons", {
    params,
  });
  return response.data;
};

/** POST body for `POST /v1/coupon/admin/coupons` (bulk create, admin). */
export type CreateCouponPayload = {
  /** ISO datetime string (use end-of-day for "expires on date X"). */
  expiry_date: string;
  couponType: string;
  /** ₦ discount amount; use `0` when the coupon is percentage-only. */
  couponAmount: number;
  /** Percent discount (0–100); use `0` when the coupon is amount-only. */
  couponDiscount: number;
  /** Hours between reuse / cooldown window (per backend contract). */
  cooldownPeriod: number;
  /** Hours the coupon stays active after issuance (per backend contract). */
  activeDuration: number;
  /** How many coupons to bulk-create (1+). */
  count: number;
  /** Vendor incentive in ₦ (referral / vendor reward side). */
  vendorIncentiveAmount: number;
  /** Vendor incentive as percentage. */
  vendorIncentivePercentage: number;
};

export type CreatedCouponItem = {
  id: string;
  couponCode: string;
  couponType: string;
  expiryDate: string;
  couponAmount: number | null;
  couponDiscount: number | null;
  cooldownPeriod: number;
  activeDuration: number;
  isSignupBonus: boolean;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    userType: string;
  };
  createdAt: string;
};

export type CreateCouponResponse = {
  data: CreatedCouponItem[];
  message: string;
  isSuccessful: boolean;
};

export const createCoupon = async (
  payload: CreateCouponPayload
): Promise<CreateCouponResponse> => {
  const response = await axiosInstance.post(
    "/v1/coupon/admin/coupons",
    payload
  );
  return response.data;
};

/**
 * PATCH body for `PATCH /v1/coupon/admin/coupons/{couponId}`.
 * Uses snake_case `expiry_date` per OpenAPI UpdateCouponRequestModel.
 *
 * Note: the API also accepts `cooldownPeriod` and `activeDuration`, but the
 * admin UI no longer exposes those fields. Re-add here when/if the form does.
 */
export type UpdateCouponPayload = {
  expiry_date?: string;
  couponAmount?: number;
  couponDiscount?: number;
  couponType?: string;
};

export type UpdatedCouponItem = {
  id: string;
  couponCode: string;
  couponType: string;
  expiryDate: string;
  couponAmount: number | null;
  couponDiscount: number | null;
  cooldownPeriod: number;
  activeDuration: number;
  isSignupBonus: boolean;
  isFirstOrderDiscount: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type UpdateCouponResponse = {
  data: UpdatedCouponItem;
  message: string;
  isSuccessful: boolean;
};

export const updateCoupon = async (
  couponId: string,
  payload: UpdateCouponPayload
): Promise<UpdateCouponResponse> => {
  const response = await axiosInstance.patch(
    `/v1/coupon/admin/coupons/${couponId}`,
    payload
  );
  return response.data;
};

// Get a single coupon by ID
export const getCouponById = async (couponId: string) => {
  const response = await axiosInstance.get(`/v1/coupon/${couponId}`);
  return response.data;
};

/**
 * POST body for `POST /v1/coupon/admin/coupons/{couponCode}/assign`.
 *
 * Backend currently accepts an array of user ids, but product rule is
 * "one coupon → one buyer". The UI enforces the single-pick rule by always
 * sending an array of length 1. When backend tightens the contract we just
 * narrow this type to `string`.
 */
export type AssignCouponPayload = {
  assignedUserId: string[];
};

export type AssignCouponResponse = {
  message: string;
  isSuccessful: boolean;
  /** Ids that were successfully assigned by this request. */
  assigned: string[];
  /** Ids the server skipped because they were already assigned. */
  alreadyAssigned: string[];
};

export const assignCoupon = async (
  couponCode: string,
  payload: AssignCouponPayload
): Promise<AssignCouponResponse> => {
  const response = await axiosInstance.post(
    `/v1/coupon/admin/coupons/${couponCode}/assign`,
    payload
  );
  return response.data;
};
